/**
 * @ngdoc Controller
 * @name FirmwareUpdateController
 */
import { handleFileDownload } from "@/utils/download";
import {
    enableUpdateChecks,
    disableUpdateChecks,
} from "@/version/update-detector";
import FirmwareFileUploadModal from "@/vue/components/modals/FirmwareFileUploadModal.vue";
import * as Sentry from "@sentry/browser";

(function () {
    "use strict";

    angular
        .module("vrm.core")
        .controller("FirmwareUpdateController", FirmwareUpdateController);

    /**
     * @constructor
     * @ngInject
     */
    function FirmwareUpdateController(
        $log,
        $scope,
        $stateParams,
        $state,
        ApiService,
        MQTTService,
        $q,
        InstallationsService,
        vrmNotificationsService,
        vrmNotificationType,
        VrmModalService,
        locale,
        $timeout,
        vrmConfig,
    ) {
        $log.info("FirmwareUpdateController");

        $scope.forms = {};
        $scope.progress = 0;
        $scope.status = "Querying product";
        $scope.updateInProgress = false;
        $scope.deviceBeingUpdated = null;
        $scope.fetchingStatusCode = "connecting";
        $scope.fetchingError = false;
        $scope.partialFetchInfo = null;
        $scope.fetchingProgress = 10;
        $scope.collapsedUpload = true;

        // Flag to prevent rescanning too quickly after the last request.
        // Rescanning too quickly can cause request already in progress errors to be shown.
        $scope.isRescanTimedOut = true;

        const rescanTimeout = 2000;

        disableUpdateChecks();
        // Entrypoint: check for updates in progress, fetch devices otherwise.
        checkForUpdateInProgress();

        function base64toBlob(base64Data, contentType) {
            contentType = contentType || "";
            var sliceSize = 1024;
            var byteCharacters = atob(base64Data);
            var bytesLength = byteCharacters.length;
            var slicesCount = Math.ceil(bytesLength / sliceSize);
            var byteArrays = new Array(slicesCount);

            for (var sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
                var begin = sliceIndex * sliceSize;
                var end = Math.min(begin + sliceSize, bytesLength);

                var bytes = new Array(end - begin);
                for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
                    bytes[i] = byteCharacters[offset].charCodeAt(0);
                }
                byteArrays[sliceIndex] = new Uint8Array(bytes);
            }
            return new Blob(byteArrays, { type: contentType });
        }

        // Check if there is an update in progress for the current installation
        // and show the modal with the progress
        function checkForUpdateInProgress() {
            InstallationsService.getInstallation($stateParams.id).then(
                function (result) {
                    redirectUnauthorizedUser(result);

                    $scope.installation = result;

                    MQTTService.setPortalId($scope.installation.identifier);
                    MQTTService.subscribe();
                    var deviceUpdateCallback = function (message) {
                        var payload = message.payload;
                        if (!$scope.updateInProgress) {
                            VrmModalService.openUpdateDeviceModal($scope);
                            $scope.updateInProgress = true;
                        }

                        $scope.vrm_update_status = payload;
                        $scope.progress = payload.progress;
                        $scope.status = payload.status;

                        if ($scope.progress < 0) {
                            $scope.updateInProgress = false;
                            updateError(
                                payload.error_message_code,
                                payload.error_message,
                                payload.error_code,
                                $scope.deviceBeingUpdated
                            );
                        }

                        if (payload.file && payload.filename) {
                            handleFileDownload(base64toBlob(payload.file), payload.filename);
                        }

                        if ($scope.progress >= 100 && $scope.updateInProgress) {
                            $scope.updateInProgress = false;

                            var successMessage = "firmware-update.update-success";
                            if (payload.file) {
                                successMessage = "firmware-update.update-success-vebus";
                            }

                            if ($scope.deviceBeingUpdated &&
                                $scope.deviceBeingUpdated.type === "swu") {
                                successMessage = "firmware-update.update-success-swu";
                            }

                            updateSuccess(successMessage);
                        }

                        $scope.$evalAsync();
                    };
                    MQTTService.subscribeToPath({
                        identifier: $scope.installation.identifier,
                        dbusServiceType: "status/vrm_update_status",
                        dbusPath: "",
                        instance: "",
                        callback: deviceUpdateCallback,
                        callbackIdentifier: "firmware_update_status",
                        prefix: "P/",
                    });

                    // wait for MQTT loading an eventual MQTT update in progress
                    $timeout(
                        function () {
                            // fetch updatable devices if no update is in progress
                            if (!$scope.updateInProgress) {
                                $scope.fetchUpdatableDevices();
                            }
                        },
                        3000,
                        false
                    );
                }
            );
        }

        const redirectUnauthorizedUser = (installation) => {
            if (
                !(installation.view_permissions.update_settings &&
                    installation.view_permissions.mqtt_rpc &&
                    installation.view_permissions.twoway)
            ) {
                InstallationsService.redirectUnauthorizedUser(installation);
            }
        }

        $scope.fetchUpdatableDevices = function () {
            // For some reason the first mqtt message in a rescan is always a 100% finished message even though we just
            // started a scan. Keep track of the freshScan flag and reset it only after we received a first proper
            // progress message (progress < 100). This way we can hide the loading UI only after we got a real finished
            // message for this scan and prevent the user from rescanning while this scan is still in progress.
            let freshScan = true;

            $scope.isRescanTimedOut = true;

            $scope.devices = null;
            $scope.fetchingError = false;
            $scope.fetchingStatus = null;
            $scope.fetchingStatusCode = "starting";
            $scope.fetchingProgress = 20;
            $scope.partialFetchInfo = null;
            var path = "/installations/" + $stateParams.id + "/list-devices";
            ApiService.call(path, null, "GET", false, "json", false)
                .then(function (response) {
                    if (response.data.success) {
                        var devicesFetchingCallback = function (message) {
                            var payload = message.payload;
                            $scope.fetchingProgress = payload.progress;

                            // Reset the freshScan flag when we get some real progress updates.
                            if (payload.progress < 100) {
                                freshScan = false;
                            }

                            if (payload.status) {
                                $scope.fetchingStatus =
                                    "Status: " + payload.status;
                                $scope.fetchingStatusCode = null;
                            } else if (payload.status_code) {
                                $scope.fetchingStatusCode = payload.status_code;
                                $scope.fetchingStatus = null;
                            }

                            // Show the list of devices (partial or complete) when the process is finished.
                            // Can also show partial list by removing the process but looks weird.
                            //
                            // Only show the list after the scan is really finished by checking the freshScan flag
                            // to make sure we don't update for the first finished message that is emitted at the
                            // beginning of a rescan.
                            if (payload.progress >= 100 && !freshScan) {
                                $scope.devices = payload.devices;

                                // Enable rescan button after a timeout to allow mqtt to settle down and reset
                                // for the next request before trying to scan again.
                                $timeout(function () {
                                    $scope.isRescanTimedOut = false;
                                }, rescanTimeout);

                                fetchDeviceDescriptions();
                            }

                            // Show a warning in template that the list is incomplete and the cause: error message and error code.
                            if (payload.progress < 0 && payload.partial) {
                                $scope.partialFetchInfo = {
                                    errorMessage: payload.error_message,
                                    errorCode: payload.error_code,
                                };
                            }

                            // Open a modal with: translated message, error message and error code.
                            if (payload.progress < 0 && !payload.partial) {
                                fetchingError(
                                    payload.error_message_code,
                                    payload.error_message,
                                    payload.error_code
                                );
                            }

                            $scope.$evalAsync();
                        };
                        MQTTService.subscribeToPath({
                            identifier: $scope.installation.identifier,
                            dbusServiceType: "status/vrm_updatable_devices",
                            dbusPath: "",
                            instance: "",
                            callback: devicesFetchingCallback,
                            callbackIdentifier: "firmware_update_devices",
                            prefix: "P/",
                        });
                    }
                })
                .catch(function (response) {
                    // Non-full-access user shouldn't be able to access this page
                    if (response.status === 403) {
                        InstallationsService.getInstallation($stateParams.id).then(function (installation) {
                            redirectUnauthorizedUser(installation);
                        });

                    }
                    if (
                        response.data &&
                        "error_message_code" in response.data
                    ) {
                        fetchingError(
                            response.data.error_message_code,
                            response.data.error_message,
                            response.data.error_code
                        );
                    }
                    $scope.fetchingError = true;
                });
        };

        function fetchDeviceDescriptions() {
            if (!$scope.devices) return;

            var path = "/device-name?";
            _.each($scope.devices, function (device) {
                if (typeof device.id !== "undefined") {
                    path += "&product_ids[]=" + device.id;
                }
            });

            ApiService.call(path, {}, "GET", false, "json", false).then(
                function (response) {
                    if (response.data.success && response.data.records) {
                        var descriptions = response.data.records;
                        _.each($scope.devices, function (device) {
                            if (descriptions[device.id]) {
                                device.description = descriptions[device.id];
                            }
                        });
                    }
                }
            );
        }

        function updateNormal(device) {
            VrmModalService.openUpdateDeviceModal($scope);
            $scope.updateInProgress = true;
            var updateData = getUpdateFormData(device);

            var path = "/installations/" + $stateParams.id + "/firmware-update";
            ApiService.call(
                path,
                updateData,
                "POST",
                false,
                "json",
                false
            )
                .then(function () {
                    // The watch for mqtt update progress triggers the modal showing.
                    $scope.deviceBeingUpdated = device;
                })
                .catch(function (response) {
                    if (
                        response.data &&
                        "error_message_code" in response.data
                    ) {
                        updateError(
                            response.data.error_message_code,
                            response.data.error_message,
                            response.data.error_code,
                            device
                        );
                    }
                });
        }

        function getUpdateFormData(device){
            let updateData = getUpdateData(device);
            let updateFormData = new FormData();

            for (let key in updateData) {
                updateFormData.append(key, updateData[key]);
            }

            return updateFormData;
        }

        function getUpdateData(device) {
            // Not everything is necessary, but used for logging in the backend
            var updateData = {
                product_ids: device.id,
                serial: device.serial ? device.serial : null,
                latest_firmware_version: device.latest_firmware_version
                    ? device.latest_firmware_version
                    : null,
                version: device.version ? device.version : null,
            };
            if (typeof device.identificationType !== "undefined") {
                updateData[device.identificationType] = device.identificator;
            }
            if (typeof device.connection !== "undefined") {
                updateData["connection_id"] = device.connection;
            }
            if (typeof device.type !== "undefined") {
                updateData["connection_type"] = device.type;
            }
            if (typeof device.service !== "undefined") {
                updateData["service"] = device.service;
            }
            return updateData;
        }

        /**
         * Check if a device needs to be updated in a batch.
         *
         * If the 'id' field contains multiple ids separated by a comma, all ids need to be updated in a batch.
         *
         * @param device
         * @returns {boolean}
         */
        $scope.isBatchUpdatableDevice = function(device) {
            return device.id && device.id.split(',').length > 1;
        }

        /**
         * Check if the user has any batch updatable devices at all.
         *
         * This is mainly used to adjust the table actions to prevent unnecessary white space.
         *
         * @returns {boolean}
         */
        $scope.hasBatchUpdatableDevice = function() {
            let result = false;

            $scope.devices.forEach((device) => {
                if (!result) {
                    result = $scope.isBatchUpdatableDevice(device);
                }
            });

            return result;
        }

        /**
         * Returns true if the given device is currently updatable
         *
         * @param device
         * @returns {Boolean}
         */
        $scope.isUpdatable = function(device) {
            const scenario = $scope.getUpdateScenario(device);
            return scenario !== 'up_to_date' && scenario !== 'not_updatable' && scenario !== 'not_in_library';
        }

        /**
         * Get the first product ID in the device's list.
         *
         * Used to display a collapsed list of product ID's for batch updatable devices (e.g. ID1 +4)
         *
         * @param device
         * @returns {String}
         */
        $scope.getFirstDeviceId = function(device) {
            const ids = $scope.getMultiDeviceIds(device);
            return ids[0];
        }

        /**
         * Get an array of product id's for the given device instead of a csv string.
         *
         * @param device
         * @returns {*}
         */
        $scope.getMultiDeviceIds = function(device) {
            return device.id.split(',');
        }

        /**
         * Checks if the given widget is currently expanded
         *
         * @param device
         */
        $scope.isExpanded = function(device) {
            return $scope.expanded === device;
        }

        /**
         * Toggle the expanded state for the given device.
         *
         * If another device is currently expanded, it will collapse.
         *
         * @param device
         */
        $scope.toggleExpanded = function (device) {
            if ($scope.isBatchUpdatableDevice(device)) {
                $scope.expanded = $scope.isExpanded(device) ? null : device;
            }
        }

        /**
         * Get the total number of product ID's connected to this device.
         *
         * @param device
         * @returns {Integer}
         */
        $scope.getDeviceIdCount = function(device) {
            const ids = device.id.split(',');
            return ids.length;
        }


        $scope.uploadBatchFiles = function(device) {
            $scope.update(device, uploadBatchFilesNormal);
        }

        async function uploadBatchFilesNormal(device) {
            // return $scope.uploadFile(device);
            let resp = await VrmModalService.openVueModal(FirmwareFileUploadModal, {
                'device': device,
                'acceptedFileTypes': ['.vff'],
            });

            // If we get data from the modal the user has selected the required files to be uploaded.
            // When no data is returned, the user has cancelled, so we do nothing.
            if (resp) {
                let data = getUpdateFormData(device);
                let firmwareBlobs = [];
                let productIds = [];

                // Convert the selected firmware files to a base64 blob and send them to the API.
                for (let key in resp) {
                    productIds.push(key);

                    let base64 = await fileToBase64(resp[key]);
                    firmwareBlobs.push(base64);
                }

                data.append("product_ids", productIds);
                data.append("firmware_files", firmwareBlobs);

                // Force updating device modal to appear before the first progress update from MQTT
                $scope.updateInProgress = true;

                // Open the update progress modal to show status updates from MQTT.
                $timeout(function () {
                    VrmModalService.openUpdateDeviceModal($scope);
                }, 510);

                // Send firmware files and info to the API to start the update.
                const path = `/installations/${$stateParams.id}/firmware-update`;

                ApiService.call(
                    path,
                    data,
                    "POST",
                    false,
                    "json",
                    false
                ).then(function () {
                    // File uploaded, the progress will be updated in the modal
                    $scope.deviceBeingUpdated = device;
                }).catch(function (response) {
                    if (response.data && "error_message_code" in response.data) {
                        updateError(
                            response.data.error_message_code,
                            response.data.error_message,
                            response.data.error_code,
                            device
                        );
                    }
                });
            }
        }

        function uploadFileNormal(device) {
            var requiredFileType =
                device.identificationType === "nad"
                    ? ["vup", "xup"]
                    : ["dup", "xup"];
            if (
                device.type &&
                (isDeviceVebus(device) || isDeviceVebusBms(device))
            ) {
                requiredFileType = ["vff"];
            }
            if (device.type && device.type === "http") {
                requiredFileType = ["evcsup"];
            }
            VrmModalService.openUploadModal(
                $scope,
                "firmware_" + requiredFileType[0],
                function (files) {
                    if (
                        files.length == 1 &&
                        requiredFileType.includes(
                            files[0].name.split(".").pop()
                        )
                    ) {
                        var file = files[0];
                        var data = getUpdateFormData(device);

                        fileToBase64(file).then(function (base64Content) {
                            data.append("firmware_files", base64Content);
                            var path =
                                "/installations/" +
                                $stateParams.id +
                                "/firmware-update";
                            // Force updating device modal to appear before the first progress update from MQTT
                            $scope.updateInProgress = true;
                            $timeout(function () {
                                VrmModalService.openUpdateDeviceModal($scope);
                            }, 510);
                            ApiService.call(
                                path,
                                data,
                                "POST",
                                false,
                                "json",
                                false
                            )
                                .then(function () {
                                    // File uploaded, the progress will be updated in the modal
                                    $scope.deviceBeingUpdated = device;
                                })
                                .catch(function (response) {
                                    if (
                                        response.data &&
                                        "error_message_code" in response.data
                                    ) {
                                        updateError(
                                            response.data.error_message_code,
                                            response.data.error_message,
                                            response.data.error_code,
                                            device
                                        );
                                    }
                                });
                        });
                    } else {
                        uploadError(requiredFileType[0]);
                    }
                },
                { productId: device.id, name: device.description }
            );
        }

        function isDeviceVebus(device) {
            return device.type === "vebus";
        }

        function isDeviceVebusBms(device) {
            return device.type === "vebus-bms";
        }

        $scope.update = function (device, callback) {
            callback = callback || updateNormal;
            let updateCallBack = function () {
                $timeout(() => {
                    callback(device);
                }, 510);
            };

            let _continue = (confirmed, callback) => {
                if (!confirmed) return;
                VrmModalService.next(callback);
            };

            if (isDeviceVebus(device)) {
                // For VE.Bus devices, a confirmation wizard should be shown
                // because the update procedure can lead to blackouts, so users need to be really sure

                let step5 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step3",
                        { checkboxVersion: true },
                        (c) => _continue(c, updateCallBack),
                        "firmware-update.confirm_maybe_restore",
                        "firmware-update.confirm_maybe_restore_description",
                        "vrm-modal--alert--wide"
                    );
                };

                let step4 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step4",
                        { checkboxVersion: true },
                        (c) => _continue(c, step5),
                        "firmware-update.confirm_bms",
                        "firmware-update.confirm_bms_description",
                        "vrm-modal--alert--wide"
                    );
                };
                let step3 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step3",
                        { checkboxVersion: true },
                        (c) => _continue(c, step4),
                        "firmware-update.confirm_power",
                        "firmware-update.confirm_power_description",
                        "vrm-modal--alert--wide"
                    );
                };
                let step2 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step2",
                        { checkboxVersion: true },
                        (c) => _continue(c, step3),
                        "firmware-update.confirm_acout",
                        "firmware-update.confirm_acout_description",
                        "vrm-modal--alert--wide"
                    );
                };
                VrmModalService.openConfirmFieldModal(
                    $scope,
                    "confirm-vebus-step1",
                    { checkboxVersion: true },
                    (c) => _continue(c, step2),
                    "firmware-update.confirm_reset",
                    "firmware-update.confirm_reset_description",
                    "vrm-modal--alert--wide"
                );
            } else if (isDeviceVebusBms(device)) {
                let step4 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step4",
                        { checkboxVersion: true },
                        (c) => _continue(c, updateCallBack),
                        "firmware-update.confirm_maybe_restore",
                        "firmware-update.confirm_maybe_restore_description",
                        "vrm-modal--alert--wide"
                    );
                };
                let step3 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step3",
                        { checkboxVersion: true },
                        (c) => _continue(c, step4),
                        "firmware-update.confirm_bms_v2",
                        "firmware-update.confirm_bms_v2_description",
                        "vrm-modal--alert--wide"
                    );
                };
                let step2 = () => {
                    VrmModalService.openConfirmFieldModal(
                        $scope,
                        "confirm-vebus-step2",
                        { checkboxVersion: true },
                        (c) => _continue(c, step3),
                        "firmware-update.confirm_power",
                        "firmware-update.confirm_power_description",
                        "vrm-modal--alert--wide"
                    );
                };
                VrmModalService.openConfirmFieldModal(
                    $scope,
                    "confirm-vebus-step1",
                    { checkboxVersion: true },
                    (c) => _continue(c, step2),
                    "firmware-update.confirm_reset",
                    "firmware-update.confirm_bms_v2_reset_description",
                    "vrm-modal--alert--wide"
                );
            } else if ($scope.showWarning(device)) {
                VrmModalService.openWarningModal(
                    $scope,
                    updateCallBack,
                    "firmware-update.continue-update",
                    "firmware-update.boot-mode-warning"
                );
            } else {
                callback(device);
            }
        };

        $scope.uploadFile = function (device) {
            $scope.update(device, uploadFileNormal);
        };

        function fileToBase64(file) {
            var deferred = $q.defer();
            var reader = new FileReader();
            reader.onload = function () {
                deferred.resolve(reader.result.split(",")[1]);
            };
            reader.readAsDataURL(file);
            return deferred.promise;
        }

        function uploadError(type) {
            // Upload modal has 500ms removalDelay that need to wait before error
            $timeout(function () {
                VrmModalService.openErrorModal(
                    $scope,
                    "firmware-update.file-upload-error_" + type
                );
            }, 510);
        }

        function updateSuccess(message) {
            $.magnificPopup.close();
            VrmModalService.openSuccessModal($scope, message);
            // Fetch device again to get the latest device data but after 5s to allow the
            // device to completely be up after update.
            $timeout(function () {
                $scope.fetchUpdatableDevices();
            }, 5000);
            // Restore initial status.
            resetProgress();
        }

        function updateError(errorMessageCode, errorMessage, errorCode, device) {
            $.magnificPopup.close();
            errorMessageCode = errorMessageCode || "update-error";
            if (errorCode === "733" && errorMessage.includes("vbdup failed with exit code 54")) {
                errorMessageCode = "update-error-update-fw";
            }
            else if (errorCode === "733" && errorMessage.includes("vbdup failed with exit code 123")) {
                errorMessageCode = "update-error-incompatible-files";
            } 

            var errorMsgKey = "firmware-update." + errorMessageCode;
            $timeout(function () {
                VrmModalService.openErrorModal(
                    $scope,
                    errorMsgKey,
                    errorMessage,
                    errorCode,
                    $scope.fetchUpdatableDevices
                );
            }, 510);

            var path = "/installations/" + $stateParams.id + "/log-event";
            var data = {};
            data.origin_request = "front";
            data.product_id = device.id;
            data.event_type = "1";
            data.event_description = "FIRMWARE_UPDATE_FAILED";
            data.error_code = errorCode;
            data.device_serial = device.serial;
            ApiService.call(path, data, "POST", false, "json", false);

            // Restore initial status.
            resetProgress();
        }

        function fetchingError(errorMessageCode, errorMessage, errorCode) {
            $.magnificPopup.close();
            errorMessageCode =
                errorMessageCode || "fetching-devices-error-full";
            errorMessageCode = errorMessageCode.split(".").join("-");

            var errorMsgKey = "firmware-update." + errorMessageCode;

            if (errorCode === '1392' || errorCode === 1392) {
                // Prevent MQTT timeout errors from being shown in a modal. Log to sentry and show a toast.
                vrmConfig.sentry === true && Sentry.withScope(function (scope) {
                    scope.setLevel('warning');
                    scope.setTag("error_type", 'mqtt_timeout');
                    scope.setFingerprint(['mqtt_timeout']);
                    Sentry.captureException(new Error(`MQTT timeout after firmware update: ${errorMessageCode}`));
                });

                locale.ready('firmware-update').then(function () {
                    vrmNotificationsService.add( {type: vrmNotificationType.ERROR, message: locale.getString(errorMsgKey) });
                });
            } else {

                VrmModalService.openErrorModal(
                    $scope,
                    errorMsgKey,
                    errorMessage,
                    errorCode
                );
            }

            $scope.fetchingError = true;
        }

        function resetProgress() {
            $scope.status = "Waiting for status updates";
            $scope.progress = 0;
            $scope.fetchingProgress = 10;
            $scope.deviceBeingUpdated = null;
        }

        $scope.$on("$destroy", function () {
            MQTTService.unsubScribeFromPath({
                identifier: $scope.$parent.installation.identifier,
                dbusServiceType: "status/vrm_update_status",
                dbusPath: "",
                instance: "",
                // callback: deviceUpdateCallback,
                callbackIdentifier: "firmware_update_status",
                prefix: "P/",
            });

            MQTTService.unsubScribeFromPath({
                identifier: $scope.installation.identifier,
                dbusServiceType: "status/vrm_updatable_devices",
                dbusPath: "",
                instance: "",
                // callback: devicesFetchingCallback,
                callbackIdentifier: "firmware_update_devices",
                prefix: "P/",
            });
            MQTTService.unsubscribe();

            enableUpdateChecks();
        });

        $scope.updatable = function (device) {
            return device.updatable;
        };

        $scope.inLibrary = function (device) {
            return device.latest_firmware_version;
        };

        $scope.showWarning = function (device) {
            return (
                $scope.bootMode(device) &&
                [
                    "104",
                    "105",
                    "106",
                    "107",
                    "108",
                    "300",
                    "A101",
                    "A040",
                    "A041",
                    "A042",
                    "A043",
                    "A044",
                    "A045",
                    "A046",
                    "A047",
                    "A048",
                    "A049",
                    "A04A",
                    "A04B",
                    "A04C",
                    "A04D",
                    "A04E",
                    "A04F",
                    "A050",
                    "A051",
                    "A052",
                    "A053",
                    "A054",
                    "A055",
                    "A056",
                    "A057",
                    "A058",
                    "A059",
                    "A05A",
                    "A05B",
                    "A05C",
                    "A05D",
                    "A05E",
                    "A05F",
                    "A060",
                    "A061",
                    "A062",
                    "A063",
                    "A064",
                    "A065",
                    "A066",
                    "A067",
                    "A068",
                    "A069",
                    "A06A",
                    "A06B",
                    "A06C",
                    "A06D",
                    "A06E",
                    "A06F",
                    "A070",
                    "A071",
                    "A072",
                    "A073",
                    "A074",
                    "A075",
                    "A076",
                    "A077",
                    "A078",
                    "A079",
                    "A07A",
                    "A07B",
                    "A07C",
                    "A07D",
                    "A07E",
                    "A07F",
                ].includes(device.id)
            );
        };

        $scope.bootMode = function (device) {
            return device.mode && device.mode === "boot";
        };

        $scope.latestVersionOrNewer = function (device) {
            return +device.versionAsDecimal >= +device.latest_firmware_number;
        };

        $scope.isGX = function (device) {
            return device.type && device.type === 'swu';
        };

        $scope.getUpdateScenario = function (device) {
            if (!$scope.updatable(device)) {
                return "not_updatable";
            } else if (!$scope.inLibrary(device)) {
                return "not_in_library";
            } else {
                // Device is updatable
                if ($scope.isBatchUpdatableDevice(device) && !$scope.latestVersionOrNewer(device))  {
                  return "updatable_batch";
                } else if ($scope.bootMode(device)) {
                    // updatable and boot mode
                    if ($scope.showWarning(device)) {
                        // updatable, boot mode, show warning
                        return "updatable_boot_mode_warning";
                    } else {
                        // updatable, boot mode, no warning
                        return "updatable_boot_mode";
                    }
                } else {
                    // GX devices can be downgraded when switching updated feeds
                    if ($scope.isGX(device)) {
                        return device.latest_firmware_version != device.version ? "update" : "up_to_date";
                    }

                    // updatable and mode is not boot
                    if ($scope.latestVersionOrNewer(device)) {
                        // updatable, device has latest or newer firmware
                        return "up_to_date";
                    } else {
                        // updatable, device version is either undefined or lower than the latest
                        return "update";
                    }
                }
            }
        };
    }
})();
